import { Button } from '@progress/kendo-react-buttons'
import { Label } from '@progress/kendo-react-labels'
import {
  Card,
  CardActions,
  CardBody,
  CardHeader,
  CardTitle,
  GridLayout,
  GridLayoutItem,
  StackLayout
} from '@progress/kendo-react-layout'
import { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MessageProvider } from '../../../services'
import { CommonActions, useAppDispatch } from '../../../stores'
import { useAccessibleAuthority, usePageTitle } from '../../../components/hooks'
import Constants from '../../../data/Constants.json'
import {
  ErrorMessages,
  ErrorMessage,
  ConfirmDialog
} from '../../../components/parts'

import { ImportDtmGateway } from '../../../gateway/building/ImportDtmGateway'
import { ImportGateway } from '../../../gateway/building/ImportGateway'
import { Authority } from '../../../gateway/system/Authority'
import { Grid, GridColumn, GridCellProps } from '@progress/kendo-react-grid'
import { Marker } from '../../../components/elements'
import { LastResult } from '@gateway.Building'
const batchId = Constants.batchId.buildingImport
const DEFAULT_STATE = {
  batchId: '',
  lastLinkDtm: '',
  startDtm: '',
  endDtm: '',
  progressStatus: '',
  message: ''
}
export const BuildingImport: FC = () => {
  const [lastLinkInfo, setLastLinkInfo] = useState<LastResult[]>([DEFAULT_STATE])
  const [success, setSuccess] = useState(false)
  const [confirmDialogVisible, setConfirmDialogVisible] = useState(false)
  const [notificationMessages, setNotificationMessages] = useState<ErrorMessage[]>([])
  const [errorMessages, setErrorMessages] = useState<ErrorMessage[]>([])

  useAccessibleAuthority(Authority.Builder)

  usePageTitle('物件情報取り込み')
  const dispatch = useAppDispatch()

  useEffect(() => {
    /**
     * 取込日時取得
     */
    dispatch(CommonActions.setIsLoading(true))
    ImportDtmGateway.getImportDtm(batchId)
      .then((res) => {
        if (!res.error) {
          const linkInfo: LastResult = {
            ...res.data.lastResult,
            lastLinkDtm: formattedImportDtm(res.data.lastResult.lastLinkDtm),
            startDtm: formattedImportDtm(res.data.lastResult.startDtm),
            endDtm: formattedImportDtm(res.data.lastResult.endDtm)
          }
          const linkInfoArray: LastResult[] = [linkInfo]
          setLastLinkInfo(linkInfoArray)
        } else {
          if (Array.isArray(res.messages) && res.messages.length > 0) {
            const errorMessages = res.messages.map<ErrorMessage>((message) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(errorMessages)
          }
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })
  }, [success])

  const navigate = useNavigate()
  const handleGoBack = () => {
    navigate(-1)
  }

  /**
   * 物件情報の取込
   */
  let flg: boolean = true
  const handleFetchIn = () => {
    dispatch(CommonActions.setIsLoading(true))
    ImportGateway.putBuildingImport()
      .then((res) => {
        if (res.error) {
          // errorの場合
          flg = false
          if (Array.isArray(res.messages) && res.messages.length > 0) {
            const errorMessages = res.messages.map<ErrorMessage>((message) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(errorMessages)
          }
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
        if (flg) {
          // 明細部を再検索する
          setSuccess(!success)
          // Notificationを表示する
          setNotificationMessages([
            {
              id: 'I017',
              content: MessageProvider.provide('I017', '物件情報取り込み')
            }
          ])
        }
      })
  }

  const opportunityProgressColor = (props: GridCellProps) => {
    // 進捗状況
    // eslint-disable-next-line react/prop-types
    const linkInfo: LastResult = props.dataItem
    const progressColor =
      // eslint-disable-next-line react/prop-types
      (linkInfo.startDtm !== null && linkInfo.endDtm === null && linkInfo.message === null)
        ? 'primary'
        // eslint-disable-next-line react/prop-types
        : (linkInfo.startDtm !== null && linkInfo.endDtm !== null)
            ? 'secondary'
            // eslint-disable-next-line react/prop-types
            : (linkInfo.startDtm !== null && linkInfo.endDtm === null && linkInfo.message !== null)
                ? 'accent'
                : undefined

    return progressColor !== undefined
      ? (<td style={{ textAlign: 'center' }}>
          <Marker themeColor={progressColor}>
            {/* eslint-disable-next-line react/prop-types */}
            {linkInfo.progressStatus}
          </Marker>
        </td>
        )
      : (
          <td></td>
        )
  }

  /**
   * 取込日時の整形
   * @param importDtm APIで取得した取込日時
   * @returns 整形した取込日時
   */
  const formattedImportDtm = (importDtm: string | undefined | null) => {
    if (importDtm === undefined || importDtm === null) return null

    const splitT = importDtm.split('T')
    const YYYY = splitT[0].split('-')[0]
    const MM = splitT[0].split('-')[1]
    const DD = splitT[0].split('-')[2]
    const hh = splitT[1].split(':')[0]
    const mm = splitT[1].split(':')[1]

    return `${YYYY}年${MM}月${DD}日  ${hh}:${mm}`
  }

  return (
    <>
      <Card style={{ width: '40%', margin: '30px auto' }}>
        <CardHeader style={{ textAlign: 'center' }}>
          <CardTitle>物件情報取り込み</CardTitle>
        </CardHeader>
        <CardBody>
          <GridLayout cols={[{ width: '20%' }]} gap={{ cols: 5, rows: 5 }}>
            <GridLayoutItem row={1} col={1}>
              <Label>前回取込日時</Label>
            </GridLayoutItem>
            <GridLayoutItem row={1} col={2}>
              {lastLinkInfo[0].lastLinkDtm}
            </GridLayoutItem>
          </GridLayout>
        </CardBody>
        <CardActions layout="end">
          <StackLayout gap={5}>
            <Button style={{ minWidth: 120 }} onClick={handleGoBack}>
              戻る
            </Button>
            <Button
              style={{ minWidth: 120 }}
              themeColor="primary"
              onClick={() => setConfirmDialogVisible(true)}
            >
              取込
            </Button>
          </StackLayout>
        </CardActions>
      </Card>
      <Grid
        style={{ width: '90%', margin: '30px auto' }}
        sortable={false}
        filterable={false}
        scrollable='none'
        data={lastLinkInfo}>
          <GridColumn
            title="開始日時"
            field="startDtm"
            width="200px"
          />
          <GridColumn
            title="終了日時"
            field="endDtm"
            width="200px"
          />
          <GridColumn
            title="実行結果"
            field="progressStatus"
            cell={opportunityProgressColor}
            width="100px"
            />
          <GridColumn
            title="メッセージ"
            field="message"
            width="calc(100% - 500px)"
          />
      </Grid>

      <ConfirmDialog
        open={confirmDialogVisible}
        renderCloseButton
        title={'確認'}
        id={'I002'}
        args={['物件情報取り込み', '実行']}
        onClickOk={() => {
          setConfirmDialogVisible(false)
          handleFetchIn()
        }}
        onClickCancel={() => {
          setConfirmDialogVisible(false)
        }}
      />
      <ErrorMessages type="notification" messages={notificationMessages} />
      <ErrorMessages type="dialog" messages={errorMessages} />
    </>
  )
}
